import { useMemo, useState } from 'react';
import { PlusIcon, TrashIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { extractCarrierErrorMessage } from '@/components/carrier/carrierErrors';
import { CountryInput } from '@/views/Events/EventOrder/PublicEventOrderForm/components/CountryInput';
import type { WarehouseRoutingFieldsFragment } from '@/graphql/fragments/orderWarehouseConfig.generated';
import { useGetOrderWarehouseConfigQuery } from '@/graphql/queries/getOrderWarehouseConfig.generated';
import { useSetOrderWarehouseConfigMutation } from '@/graphql/mutations/setOrderWarehouseConfig.generated';
import { useWarehousesQuery } from '@/graphql/queries/warehouses.generated';
import i18n from '@/utils/i18n';

const BUILT_IN_DEFAULT = '__built_in__';

type Warehouse = { id: string; name?: string | null };

type CountryEntry = {
  country_code: string;
  warehouse_uuid: string | null;
};

type RoutingState = {
  defaultWarehouseUuid: string | null;
  countries: CountryEntry[];
};

type ConfigState = { nonEvent: RoutingState; event: RoutingState };

function getCountryDisplayName(code: string): string {
  try {
    return (
      new Intl.DisplayNames([i18n.language || 'en'], { type: 'region' }).of(
        code,
      ) || code
    );
  } catch {
    return code;
  }
}

function warehouseName(warehouses: Warehouse[], id: string): string {
  return warehouses.find((w) => w.id === id)?.name ?? id;
}

function sortedCountries(countries: CountryEntry[]): CountryEntry[] {
  return [...countries].sort((a, b) =>
    a.country_code.localeCompare(b.country_code),
  );
}

function snapshot(state: ConfigState): string {
  const normalize = (routing: RoutingState) => ({
    ...routing,
    countries: sortedCountries(routing.countries),
  });
  return JSON.stringify([normalize(state.nonEvent), normalize(state.event)]);
}

function fromRouting(routing: WarehouseRoutingFieldsFragment): RoutingState {
  return {
    defaultWarehouseUuid: routing.default_warehouse_uuid ?? null,
    countries: routing.countries.map((c) => ({
      country_code: c.country_code,
      warehouse_uuid: c.warehouse_uuid,
    })),
  };
}

function toRoutingInput(routing: RoutingState) {
  return {
    default_warehouse_uuid: routing.defaultWarehouseUuid,
    countries: routing.countries.map((c) => ({
      country_code: c.country_code,
      warehouse_uuid: c.warehouse_uuid!,
    })),
  };
}

export default function WarehouseSettingsPage() {
  const { data, loading, refetch } = useGetOrderWarehouseConfigQuery({
    fetchPolicy: 'network-only',
  });
  const { data: warehousesData, loading: warehousesLoading } =
    useWarehousesQuery();

  if ((loading && !data) || (warehousesLoading && !warehousesData)) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8">
        <Spinner className="tw:size-6" />
      </div>
    );
  }

  if (!data) return null;
  const config = data.getOrderWarehouseConfig;

  return (
    <WarehouseSettingsEditor
      key={JSON.stringify(config)}
      initial={{
        nonEvent: fromRouting(config.non_event),
        event: fromRouting(config.event),
      }}
      fallbacks={{
        nonEvent: config.non_event.fallback_warehouse_uuid,
        event: config.event.fallback_warehouse_uuid,
      }}
      warehouses={warehousesData?.warehouses ?? []}
      onSaved={() => refetch()}
    />
  );
}

type EditorProps = {
  initial: ConfigState;
  fallbacks: { nonEvent: string; event: string };
  warehouses: Warehouse[];
  onSaved: () => void;
};

function WarehouseSettingsEditor({
  initial,
  fallbacks,
  warehouses,
  onSaved,
}: EditorProps) {
  const [saveMutation, { loading: saving }] =
    useSetOrderWarehouseConfigMutation();
  const [state, setState] = useState<ConfigState>(initial);

  const isDirty = useMemo(
    () => snapshot(state) !== snapshot(initial),
    [state, initial],
  );
  const hasUnassignedCountry = [state.nonEvent, state.event].some((routing) =>
    routing.countries.some((c) => c.warehouse_uuid == null),
  );

  const handleSave = async () => {
    try {
      await saveMutation({
        variables: {
          input: {
            non_event: toRoutingInput(state.nonEvent),
            event: toRoutingInput(state.event),
          },
        },
      });
      toast.success(i18n.t('WarehouseSettings:saveSuccess'));
      onSaved();
    } catch (err) {
      toast.error(
        `${i18n.t('WarehouseSettings:saveError')} ${extractCarrierErrorMessage(err)}`,
      );
    }
  };

  return (
    <div className="tw:p-6 tw:flex tw:flex-col tw:gap-4 tw:max-w-4xl">
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-4">
        <div>
          <h2 className="tw:text-xl tw:font-medium">
            {i18n.t('WarehouseSettings:title')}
          </h2>
          <p className="tw:text-muted-foreground tw:text-sm tw:mt-1">
            {i18n.t('WarehouseSettings:intro')}
          </p>
        </div>
        <Button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || hasUnassignedCountry || saving}
        >
          {saving ? <Spinner /> : null}
          {saving ? i18n.t('Common:saving') : i18n.t('Common:save')}
        </Button>
      </div>

      {isDirty && (
        <p className="tw:text-amber-600 tw:text-sm">
          {hasUnassignedCountry
            ? i18n.t('WarehouseSettings:countryWithoutWarehouse')
            : i18n.t('WarehouseSettings:unsavedChanges')}
        </p>
      )}

      <RoutingSection
        title={i18n.t('WarehouseSettings:nonEventOrdersTitle')}
        description={i18n.t('WarehouseSettings:nonEventOrdersIntro')}
        value={state.nonEvent}
        onChange={(nonEvent) => setState((prev) => ({ ...prev, nonEvent }))}
        fallbackWarehouseUuid={fallbacks.nonEvent}
        warehouses={warehouses}
        disabled={saving}
      />
      <RoutingSection
        title={i18n.t('WarehouseSettings:eventOrdersTitle')}
        description={i18n.t('WarehouseSettings:eventOrdersIntro')}
        value={state.event}
        onChange={(event) => setState((prev) => ({ ...prev, event }))}
        fallbackWarehouseUuid={fallbacks.event}
        warehouses={warehouses}
        disabled={saving}
      />
    </div>
  );
}

type RoutingSectionProps = {
  title: string;
  description: string;
  value: RoutingState;
  onChange: (next: RoutingState) => void;
  fallbackWarehouseUuid: string;
  warehouses: Warehouse[];
  disabled: boolean;
};

function RoutingSection({
  title,
  description,
  value,
  onChange,
  fallbackWarehouseUuid,
  warehouses,
  disabled,
}: RoutingSectionProps) {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newCountry, setNewCountry] = useState<string | undefined>(undefined);

  const closeAddDialog = () => {
    setAddDialogOpen(false);
    setNewCountry(undefined);
  };

  const handleAddCountry = () => {
    if (!newCountry) return;
    if (!value.countries.some((c) => c.country_code === newCountry)) {
      onChange({
        ...value,
        countries: [
          ...value.countries,
          { country_code: newCountry, warehouse_uuid: null },
        ],
      });
    }
    closeAddDialog();
  };

  const setCountryWarehouse = (countryCode: string, warehouseUuid: string) =>
    onChange({
      ...value,
      countries: value.countries.map((c) =>
        c.country_code === countryCode
          ? { ...c, warehouse_uuid: warehouseUuid }
          : c,
      ),
    });

  const removeCountry = (countryCode: string) => {
    if (
      !window.confirm(
        i18n.t('WarehouseSettings:removeCountryConfirm', {
          country: getCountryDisplayName(countryCode),
        }),
      )
    )
      return;
    onChange({
      ...value,
      countries: value.countries.filter((c) => c.country_code !== countryCode),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
        <div className="tw:col-start-2 tw:row-span-2 tw:row-start-1 tw:self-start tw:justify-self-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAddDialogOpen(true)}
            disabled={disabled}
          >
            <PlusIcon /> {i18n.t('WarehouseSettings:addCountry')}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="tw:flex tw:flex-col tw:gap-3">
        <div className="tw:grid tw:grid-cols-[minmax(0,12rem)_1fr_2.25rem] tw:items-center tw:gap-3">
          <p className="tw:text-sm tw:font-medium">
            {i18n.t('WarehouseSettings:defaultWarehouse')}
          </p>
          <Select
            value={value.defaultWarehouseUuid ?? BUILT_IN_DEFAULT}
            onValueChange={(next) =>
              onChange({
                ...value,
                defaultWarehouseUuid: next === BUILT_IN_DEFAULT ? null : next,
              })
            }
            disabled={disabled}
          >
            <SelectTrigger className="tw:w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="start">
              <SelectItem value={BUILT_IN_DEFAULT}>
                {i18n.t('WarehouseSettings:builtInDefault', {
                  warehouse: warehouseName(warehouses, fallbackWarehouseUuid),
                })}
              </SelectItem>
              {warehouses.map((w) => (
                <SelectItem key={w.id} value={w.id}>
                  {w.name ?? w.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span />

          {sortedCountries(value.countries).map((entry) => (
            <CountryRow
              key={entry.country_code}
              entry={entry}
              warehouses={warehouses}
              disabled={disabled}
              onWarehouseChange={(next) =>
                setCountryWarehouse(entry.country_code, next)
              }
              onRemove={() => removeCountry(entry.country_code)}
            />
          ))}
        </div>
        {value.countries.length === 0 && (
          <p className="tw:text-muted-foreground tw:text-sm">
            {i18n.t('WarehouseSettings:noCountriesDescription')}
          </p>
        )}
      </CardContent>

      <Dialog
        open={addDialogOpen}
        onOpenChange={(open) =>
          open ? setAddDialogOpen(true) : closeAddDialog()
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{i18n.t('WarehouseSettings:addCountry')}</DialogTitle>
            <DialogDescription>
              {i18n.t('WarehouseSettings:addCountryDialogDescription')}
            </DialogDescription>
          </DialogHeader>
          <CountryInput
            value={newCountry}
            onChange={setNewCountry}
            placeholder={i18n.t('WarehouseSettings:selectCountry')}
            triggerClassName="tw:w-full"
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeAddDialog}>
              {i18n.t('Common:cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleAddCountry}
              disabled={!newCountry}
            >
              {i18n.t('Common:add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

type CountryRowProps = {
  entry: CountryEntry;
  warehouses: Warehouse[];
  disabled: boolean;
  onWarehouseChange: (warehouseUuid: string) => void;
  onRemove: () => void;
};

function CountryRow({
  entry,
  warehouses,
  disabled,
  onWarehouseChange,
  onRemove,
}: CountryRowProps) {
  return (
    <>
      <p className="tw:text-sm tw:truncate">
        {getCountryDisplayName(entry.country_code)}{' '}
        <span className="tw:text-muted-foreground">({entry.country_code})</span>
      </p>
      <Select
        value={entry.warehouse_uuid ?? ''}
        onValueChange={onWarehouseChange}
        disabled={disabled}
      >
        <SelectTrigger className="tw:w-full">
          <SelectValue
            placeholder={i18n.t('WarehouseSettings:selectWarehouse')}
          />
        </SelectTrigger>
        <SelectContent align="start">
          {warehouses.map((w) => (
            <SelectItem key={w.id} value={w.id}>
              {w.name ?? w.id}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        disabled={disabled}
        aria-label={i18n.t('WarehouseSettings:removeCountry')}
      >
        <TrashIcon />
      </Button>
    </>
  );
}
